import { t } from 'i18next';

import { DocumentType, RelationshipActionRequired, RelationshipState } from 'support/types';
import { components } from 'support/types/schema-portal';
import { buildDocumentTypeFilter } from 'components/Filters/helpers';
import { FilterValue, PropertyFilter, PropertyFilterType } from 'components/ListTable/types';

export enum ConnectionsFilterKey {
  messageType = 'message_type',
  actionRequired = 'action_required',
  partnerName = 'partner_name',
  partnerId = 'partner_id',
  deactivated_partners = 'deactivated_partners',
  state = 'state',
}

export const getDefaultFilterConfig = ({
  checklistEnabled = false,
}: { checklistEnabled?: boolean } = {}): Array<PropertyFilter> => [
  {
    key: ConnectionsFilterKey.partnerName,
    type: PropertyFilterType.search,
    placeholder: t('connections:list.filter.search.label'),
    value: '',
  },
  buildDocumentTypeFilter(ConnectionsFilterKey.messageType, [DocumentType.unknown]),
  {
    key: ConnectionsFilterKey.state,
    type: PropertyFilterType.dropdown,
    label: t('connections:list.filter.relationshipState.label'),
    value: '',
    dropDownOptions: {
      multiple: true,
      dropdownValues: [
        {
          label: t('connections:list.filter.relationshipState.requested.label'),
          value: RelationshipState.REQUESTED,
        },
        {
          label: t('connections:list.filter.relationshipState.inTesting.label'),
          value: RelationshipState.IN_TESTING,
        },
        {
          label: t('connections:list.filter.relationshipState.ready.label'),
          value: RelationshipState.READY,
        },
        {
          label: t('connections:list.filter.relationshipState.deactivated.label'),
          value: RelationshipState.DEACTIVATED,
        },
        {
          label: t('connections:list.filter.relationshipState.rejected.label'),
          value: RelationshipState.REJECTED,
        },
        {
          label: t('connections:list.filter.relationshipState.blocked.label'),
          value: RelationshipState.BLOCKED,
        },
      ],
    },
  } satisfies PropertyFilter<components['schemas']['RelationshipState']>,
  {
    key: ConnectionsFilterKey.actionRequired,
    type: PropertyFilterType.dropdown,
    label: t('connections:list.filter.actionRequired.label'),
    value: '',
    dropDownOptions: {
      multiple: true,
      dropdownDefaultLabel: t('connections:list.filter.actionRequired.placeholder'),
      dropdownValues: [
        {
          label: t('connections:list.filter.actionRequired.testDocumentRequired.label'),
          value: 'READY_FOR_TESTING',
        },
        {
          label: t('connections:list.filter.actionRequired.approvalRequired.label'),
          value: 'HAS_TEST_MESSAGES;IN_REVIEW',
        },
        ...(checklistEnabled
          ? [
              {
                label: t('connections:list.filter.actionRequired.pendingTasks.label'),
                value: 'TASKS_PENDING' as RelationshipActionRequired,
              },
            ]
          : []),
      ],
    },
  } satisfies PropertyFilter<RelationshipActionRequired>,
  {
    key: ConnectionsFilterKey.deactivated_partners,
    type: PropertyFilterType.checkbox,
    label: t('connections:list.filter.deactivated_partners.label'),
    value: 'false',
  },
];

export const isDefaultFilterValue = (filterValues: Array<FilterValue>): boolean => {
  const defaultConfig = getDefaultFilterConfig();

  return areSameFilterValue(filterValues, defaultConfig);
};

export const areSameFilterValue = (filtersA: Array<FilterValue>, filtersB: Array<FilterValue>): boolean => {
  const activeFiltersA = filtersA.filter((filter) => filter.value !== '');
  const activeFiltersB = filtersB.filter((filter) => filter.value !== '');
  if (activeFiltersA.length !== activeFiltersB.length) {
    return false;
  }

  return activeFiltersA.every((filterA) =>
    activeFiltersB.some((filterB) => filterA.key === filterB.key && filterA.value === filterB.value),
  );
};
